import { inject, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { UserRoleService } from '../../../services/user-role.service';
import { StaffSignInsService } from '../../../services/staff-sign-ins.service';
import { ResetAuthenticatorResponse, UserPinScope, UserPinStatus, UserSummary } from '../../../models/user.model';
import { UserPlanSummary } from '../../../models/subscription.model';
import {
  StaffSignInSession,
  staffSignInAppLabel,
  staffSignInEndedByLabel,
  staffSignInMethodLabel
} from '../../../models/staff-sign-ins.model';
import { AuthService } from '../../../services/auth/auth.service';
import { requiresAuthenticator } from '../../../shared/roles.constants';

@Component({
  selector: 'app-user-profile',
  standalone: false,
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css']
})
export class UserProfileComponent {
  readonly staffAuth = inject(AuthService);
  userId: string | null = null;
  user: UserSummary | null = null;
  isLoading = false;

  /**
   * Edit mode. The page opens view-only; the edit bar takes this user's edit lease, so only one
   * admin at a time can change them. Credential resets count as changes too.
   */
  editMode = false;

  // Inline edit of the Account panel
  isEditing = false;
  isSaving = false;
  editError = '';
  editMessage = '';
  editForm: FormGroup;

  // Subscription plans (any status) for this user
  userPlans: UserPlanSummary[] = [];
  plansLoading = false;
  plansError = '';

  /** Whether to offer the admin audit trail for this account. */
  get canSeeAuditTrail(): boolean { return this.authService.can('Platform.Audit.View'); }

  // Sign-in password. Only ever replaced, never shown: the identity server stores a hash.
  confirmPasswordReset = false;
  passwordResetting = false;
  passwordError = '';
  passwordMessage = '';
  /** The temporary password, held only until the admin dismisses it. */
  tempPassword: string | null = null;
  /** The reset worked, but the account is disabled, so the new password cannot sign in yet. */
  tempPasswordAccountDisabled = false;

  // POS PIN. Everyone with admin access can see the state. Support can reset a merchant
  // user's PIN - the everyday "they forgot it" case - but a platform staff PIN clears
  // approvals at every merchant, so issuing one stays with SuperAdmin.
  pinStatus: UserPinStatus | null = null;
  pinLoading = false;
  pinError = '';
  pinMessage = '';
  /** Business id of the chosen scope; null until the status loads or when there is none. */
  selectedPinScopeId: string | null = null;
  confirmPinReset = false;
  pinResetting = false;
  pinEmailing = false;
  /** The new PIN, held only until the admin dismisses it — it cannot be read back. */
  newPin: string | null = null;
  newPinScopeLabel = '';

  // Authenticator app. Platform staff must sign in with one; a reset is for a lost phone and is
  // SuperAdmin-only - it signs the person out everywhere and sends them through set-up again.
  confirmAuthenticatorReset = false;
  authenticatorReason = '';
  authenticatorResetting = false;
  authenticatorError = '';
  authenticatorMessage = '';
  /** The reset worked but left something to finish (saved sign-ins not all cleared). */
  authenticatorWarning = '';

  // The staff sign-in log, filtered to this person. SuperAdmin-only server-side.
  recentSignIns: StaffSignInSession[] = [];
  signInsLoading = false;
  signInsError = '';

  constructor(
    private route: ActivatedRoute,
    private userRoleService: UserRoleService,
    private staffSignInsService: StaffSignInsService,
    private authService: AuthService,
    private formBuilder: FormBuilder
  ) {
    this.editForm = this.formBuilder.group({
      firstName: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(100)] }),
      lastName: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(100)] }),
      phoneNumber: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.maxLength(30)] })
    });
  }

  ngOnInit() {
    this.userId = this.route.snapshot.paramMap.get('userId');
    if (!this.userId) return;
    this.isLoading = true;
    this.userRoleService.getUsers().subscribe(users => {
      this.user = (users || []).find(u => u.userId === this.userId) || null;
      if (this.showRecentSignIns && this.userId) this.loadRecentSignIns(this.userId);
    }).add(() => {
      this.isLoading = false;
    });
    this.loadUserPlans(this.userId);
    this.loadPinStatus(this.userId);
  }

  private loadUserPlans(userId: string): void {
    this.plansLoading = true;
    this.plansError = '';
    this.userRoleService.getUserPlans(userId).subscribe({
      next: (plans) => (this.userPlans = plans || []),
      error: () => (this.plansError = 'Failed to load subscription plans.')
    }).add(() => (this.plansLoading = false));
  }

  // ── Password ───────────────────────────────────────────────────────────

  /**
   * Platform staff hold their roles in the global role table, which is what the users
   * endpoint returns here — so a SuperAdmin or Support row means this account can approve
   * at any merchant and open this portal. The API enforces the same rule; this only keeps
   * buttons off the screen that would answer 403.
   */
  get canEditProfile(): boolean {
    return !!this.user && this.authService.can('Users.EditProfile') && this.authService.canChangeAccount(this.user);
  }

  get isSelf(): boolean {
    return !!this.userId && this.userId === this.authService.getUserId();
  }

  get canResetPassword(): boolean {
    if (!this.user || this.isSelf) return false;
    return this.authService.can('Users.ResetCredentials') && this.authService.canChangeAccount(this.user);
  }

  requestPasswordReset(): void {
    if (!this.canResetPassword) return;
    if (!this.staffAuth.can('Users.ResetCredentials')) return;
    this.confirmPasswordReset = true;
    this.passwordError = '';
    this.passwordMessage = '';
  }

  /** The edit bar took or gave back the lease. Leaving edit mode drops anything half-done. */
  onEditModeChange(editing: boolean): void {
    this.editMode = editing;
    if (!editing) {
      this.cancelEdit();
      this.cancelPasswordReset();
      this.cancelPinReset();
      this.cancelAuthenticatorReset();
    }
  }

  cancelPasswordReset(): void {
    this.confirmPasswordReset = false;
  }

  confirmResetPassword(): void {
    if (!this.canResetPassword) return;
    if (!this.staffAuth.can('Users.ResetCredentials')) return;
    if (!this.userId || this.passwordResetting) return;

    this.passwordResetting = true;
    this.passwordError = '';
    this.passwordMessage = '';

    this.userRoleService.resetUserPassword(this.userId).subscribe({
      next: (response) => {
        this.confirmPasswordReset = false;
        this.tempPassword = response?.tempPassword || null;
        this.tempPasswordAccountDisabled = !!response?.accountDisabled;
      },
      error: (error: HttpErrorResponse) => {
        this.passwordError = error?.error?.message
          || (error?.status === 403 ? 'You cannot reset this account’s password.' : 'Failed to reset the password.');
      }
    }).add(() => (this.passwordResetting = false));
  }

  /** Clears the one-time password from the screen once it has been handed over. */
  dismissTempPassword(): void {
    this.tempPassword = null;
    this.passwordMessage = this.tempPasswordAccountDisabled
      ? 'Password reset. The account is still disabled, so it cannot sign in until it is enabled.'
      : 'Password reset.';
    this.tempPasswordAccountDisabled = false;
  }

  // ── Authenticator ──────────────────────────────────────────────────────

  /**
   * Must this account sign in with an authenticator app? The API answers from its own role list
   * (PlatformStaffSecurity:Roles); an older API that does not send the flag gets the same answer
   * from the roles it returns. A different question from canChangeAccount, which decides who
   * may change whom.
   */
  get targetRequiresAuthenticator(): boolean {
    if (!this.user) return false;
    return this.user.requiresAuthenticator
      ?? requiresAuthenticator((this.user.roles || []).map(role => (role.roleName || '').trim()));
  }

  /** Set up and in use, as Identity records it. */
  get authenticatorOn(): boolean {
    return !!this.user?.twoFactorEnabled;
  }

  /**
   * Gated like Reset password, then tighter: staff-account management (Platform.Staff.Manage,
   * so Support never sees the button), the rank rule, never your own account, and only where an
   * authenticator is required at all. The API enforces the same; this only keeps a button off the
   * screen that would answer 403.
   */
  get canResetAuthenticator(): boolean {
    if (!this.user || this.isSelf || !this.authService.can('Platform.Staff.Manage')) return false;
    return this.authService.canChangeAccount(this.user) && this.targetRequiresAuthenticator;
  }

  /**
   * The sign-in log holds only staff, so the panel needs Security.View (the staff sign-in log
   * page's permission) and a staff account - Support would only see the log's 403.
   */
  get showRecentSignIns(): boolean {
    return this.authService.can('Security.View') && this.targetRequiresAuthenticator;
  }

  requestAuthenticatorReset(): void {
    this.confirmAuthenticatorReset = true;
    this.authenticatorReason = '';
    this.authenticatorError = '';
    this.authenticatorMessage = '';
    this.authenticatorWarning = '';
  }

  cancelAuthenticatorReset(): void {
    this.confirmAuthenticatorReset = false;
    this.authenticatorError = '';
  }

  confirmResetAuthenticator(): void {
    if (!this.userId || !this.editMode || this.authenticatorResetting || !this.canResetAuthenticator) return;
    const reason = this.authenticatorReason.trim() || null;

    this.authenticatorResetting = true;
    this.authenticatorError = '';
    this.authenticatorMessage = '';
    this.authenticatorWarning = '';

    this.userRoleService.resetAuthenticator(this.userId, reason).subscribe({
      next: (response: ResetAuthenticatorResponse) => {
        this.confirmAuthenticatorReset = false;
        // Reflect the new state without a second round trip: they set it up again at next sign-in.
        if (this.user) this.user.twoFactorEnabled = false;
        this.authenticatorMessage = response?.message
          || 'Authenticator reset. They set it up again at their next sign-in.';
        this.authenticatorWarning = response?.warning || '';
        // The reset itself is written to the log, so the panel below has a new row to show.
        if (this.showRecentSignIns && this.userId) this.loadRecentSignIns(this.userId);
      },
      error: (error: HttpErrorResponse) => {
        this.authenticatorError = error?.error?.message
          || (error?.status === 403 ? 'Only a SuperAdmin can reset a staff authenticator.' : 'Failed to reset the authenticator.');
      }
    }).add(() => (this.authenticatorResetting = false));
  }

  // ── Recent sign-ins ────────────────────────────────────────────────────

  private loadRecentSignIns(userId: string): void {
    this.signInsLoading = true;
    this.signInsError = '';
    this.staffSignInsService.search({ view: 'sessions', userId, page: 1, pageSize: 5 }).subscribe({
      next: (result) => (this.recentSignIns = result?.items || []),
      error: () => (this.signInsError = 'Failed to load recent sign-ins.')
    }).add(() => (this.signInsLoading = false));
  }

  signInEndedLabel(session: StaffSignInSession): string {
    const label = staffSignInEndedByLabel(session.endedBy);
    return session.endedBy === 'admin' && session.endedByActorUserId
      ? `${label} · ${session.endedByActorUserId}`
      : label;
  }

  /** Pill colour for how a session ended; an active session keeps the default accent. */
  signInEndedClass(session: StaffSignInSession): string {
    switch (session.endedBy) {
      case 'signed_out': return 'pill-green';
      case 'time_limit': return 'pill-grey';
      case 'admin': return 'pill-amber';
      case 'token_refused': return 'pill-red';
      default: return '';
    }
  }

  signInMethodLabel(method?: string | null): string {
    return staffSignInMethodLabel(method);
  }

  signInAppLabel(app?: string | null): string {
    return staffSignInAppLabel(app);
  }

  // ── POS PIN ────────────────────────────────────────────────────────────

  private loadPinStatus(userId: string): void {
    this.pinLoading = true;
    this.pinError = '';
    this.userRoleService.getUserPinStatus(userId).subscribe({
      next: (status) => {
        this.pinStatus = status;
        // Preselect when there is nothing to choose, so the common single-PIN case is a
        // one-click reset rather than a dropdown nobody needs to read.
        this.selectedPinScopeId = status?.scopes?.length === 1 ? status.scopes[0].businessId : null;
      },
      error: () => (this.pinError = 'Failed to load PIN status.')
    }).add(() => (this.pinLoading = false));
  }

  get canResetPin(): boolean {
    if (!this.pinStatus) return false;
    return this.canResetPassword;
  }

  get pinScopes(): UserPinScope[] {
    return this.pinStatus?.scopes || [];
  }

  get selectedPinScope(): UserPinScope | null {
    return this.pinScopes.find(s => s.businessId === this.selectedPinScopeId) || null;
  }

  /** What the chosen PIN unlocks, spelled out so nobody resets the wrong one. */
  scopeReach(scope: UserPinScope): string {
    if (scope.kind === 'platform') return 'Works at every business on the platform.';
    if (scope.kind === 'shared') {
      return scope.businessCount === 1
        ? 'Works at this business.'
        : `Works at all ${scope.businessCount} of this user's businesses.`;
    }
    return 'This business requires its own PIN; only it is affected.';
  }

  requestPinReset(): void {
    if (!this.canResetPin) return;
    if (!this.staffAuth.can('Users.ResetCredentials')) return;
    if (!this.selectedPinScopeId) return;
    this.confirmPinReset = true;
    this.pinError = '';
    this.pinMessage = '';
  }

  cancelPinReset(): void {
    this.confirmPinReset = false;
  }

  confirmResetPin(): void {
    if (!this.canResetPin) return;
    if (!this.staffAuth.can('Users.ResetCredentials')) return;
    if (!this.userId || !this.selectedPinScopeId || this.pinResetting) return;

    this.pinResetting = true;
    this.pinError = '';
    this.pinMessage = '';

    this.userRoleService.resetUserPin(this.userId, this.selectedPinScopeId).subscribe({
      next: (response) => {
        this.confirmPinReset = false;
        this.newPin = response?.pin || null;
        this.newPinScopeLabel = response?.scope?.label || '';
        // Reflect that the scope now holds a PIN without a second round trip.
        const scope = this.selectedPinScope;
        if (scope) scope.hasPin = true;
        if (this.pinStatus) this.pinStatus.hasPin = true;
      },
      error: (error: HttpErrorResponse) => {
        this.pinError = error?.error?.message
          || (error?.status === 403 ? 'You cannot reset this account’s PIN.' : 'Failed to reset the PIN.');
      }
    }).add(() => (this.pinResetting = false));
  }

  /** Clears the one-time PIN from the screen once it has been handed over. */
  dismissNewPin(): void {
    this.newPin = null;
    this.newPinScopeLabel = '';
    this.pinMessage = 'PIN reset.';
  }

  emailPinSetup(): void {
    if (!this.canResetPin) return;
    if (!this.staffAuth.can('Users.ResetCredentials')) return;
    if (!this.userId || !this.selectedPinScopeId || this.pinEmailing) return;

    this.pinEmailing = true;
    this.pinError = '';
    this.pinMessage = '';

    this.userRoleService.sendUserPinSetupEmail(this.userId, this.selectedPinScopeId).subscribe({
      next: (response) => {
        this.pinMessage = response?.message || 'PIN setup email sent.';
      },
      error: (error: HttpErrorResponse) => {
        this.pinError = error?.error?.message
          || (error?.status === 403 ? 'You cannot reset this account’s PIN.' : 'Failed to send the setup email.');
      }
    }).add(() => (this.pinEmailing = false));
  }

  startEdit(): void {
    if (!this.canEditProfile) return;
    if (!this.staffAuth.can('Users.EditProfile')) return;
    if (!this.user) return;
    this.editError = '';
    this.editMessage = '';
    this.editForm.reset({
      firstName: this.user.firstName || '',
      lastName: this.user.lastName || '',
      phoneNumber: this.user.phoneNumber || ''
    });
    this.isEditing = true;
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.isSaving = false;
    this.editError = '';
  }

  /** True once the user has touched a field they left invalid. */
  invalid(controlName: string): boolean {
    const control = this.editForm.get(controlName);
    return !!control && control.invalid && control.touched;
  }

  saveProfile(): void {
    if (!this.canEditProfile) return;
    if (!this.staffAuth.can('Users.EditProfile')) return;
    if (this.isSaving || !this.userId || !this.user) return;

    this.editForm.markAllAsTouched();
    if (this.editForm.invalid) {
      this.editError = 'A first and last name are required.';
      return;
    }

    const value = this.editForm.getRawValue();
    const request = {
      firstName: (value.firstName || '').trim(),
      lastName: (value.lastName || '').trim(),
      phoneNumber: (value.phoneNumber || '').trim() || null
    };

    this.isSaving = true;
    this.editError = '';
    this.editMessage = '';

    this.userRoleService.updateUserProfile(this.userId, request).subscribe({
      next: (updated) => {
        // Reflect what the server actually stored, not what was typed.
        this.user = {
          ...this.user!,
          firstName: updated?.firstName ?? request.firstName,
          lastName: updated?.lastName ?? request.lastName,
          phoneNumber: updated?.phoneNumber ?? request.phoneNumber
        };
        this.isEditing = false;
        this.editMessage = 'Profile updated.';
      },
      error: (error: HttpErrorResponse) => {
        this.editError = error?.error?.message || 'Failed to update the profile.';
      }
    }).add(() => (this.isSaving = false));
  }

  planStatusLabel(plan: UserPlanSummary): string {
    const status = (plan.status || '').toLowerCase();
    if (status === 'trial') return 'Trial';
    if (plan.isActive) return 'Active';
    return plan.status ? plan.status.charAt(0).toUpperCase() + plan.status.slice(1) : 'Inactive';
  }

  planStatusClass(plan: UserPlanSummary): string {
    const status = (plan.status || '').toLowerCase();
    if (status === 'trial') return 'trial';
    if (plan.isActive) return 'ok';
    return 'inactive';
  }
}
